/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import DefaultApiLimit from '../../config/defaultLimit';
import {
  PricingList,
  FetchDynamicPriceProperties,
  RequestDynamicPriceInterface,
  DynamicPriceInitialProperties,
  FilterDynamicPriceInterface,
} from '../../interface/dynamic-pricing.interface';
import ConvertUTCtoDate from '../../shared-functions/ConvertUTCtoDate';
// import ConvertUTCtoHHMM from '../../shared-functions/ConvertUTCtoHHMM';
// import { FormatUtcString } from '../../shared-functions/FormatUtcString';
import dynamicPrizeApi from '../../utils/api/dynamic-prize';

// a thunk to fetch stations
export const fetchAllDynamicPrice = createAsyncThunk<
  FetchDynamicPriceProperties,
  RequestDynamicPriceInterface
>(
  'offPeak-pricing-management/fetchAllDynamicPrice',
  async (data: RequestDynamicPriceInterface, { dispatch }) => {
    try {
      const response = await dynamicPrizeApi.fetchDynamicPrize(data);
      return response.data;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        },
      });
      throw error;
    }
  },
);

// Initial state
const initialState: DynamicPriceInitialProperties = {
  pricing: [],
  loading: false,
  page: 1,
  limit: DefaultApiLimit.limit,
  totalPages: 1,
  totalResults: 0,
  filter: {},
  isFilterUpdated: false,
  sortBy: '',
  sortField: '',
};

// Create the slice
export const dynamicOffPeakPriceSlice = createSlice({
  name: 'offPeak-pricing-management',
  initialState,
  reducers: {
    /**
     * Change the device point status function
     * @param state
     * @param action
     */
    setOffPeakDynamicFilterData: (
      state: DynamicPriceInitialProperties,
      action: PayloadAction<FilterDynamicPriceInterface>,
    ) => {
      state.filter = action.payload;
      state.isFilterUpdated = true;
    },
    /**
     * Function that reset slice to initial state
     * @param state
     */
    resetOffPeakToInitialState: (state) => {
      Object.assign(state, initialState);
    },
    /**
     * function to clear date filter
     * @param state
     */
    removeDateFilters: (state) => {
      state.filter = {};
    },
    /**
     * Function to change sortBy
     * @param state
     * @param action
     */
    setSortData: (
      state,
      action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' | '' }>,
    ) => {
      state.sortField = action.payload.sortField;
      state.sortBy = action.payload.sortBy;
    },
  },
  /**
   * Extra reducers to handle thunk request
   * @param builder
   */
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllDynamicPrice.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAllDynamicPrice.fulfilled, (state, action) => {
        state.loading = false;
        state.page = action.payload.page;
        state.limit = action.payload.limit;
        state.totalPages = action.payload.totalPages;
        state.totalResults = action.payload.totalResults;
        state.pricing = action.payload.results?.map((priceData: PricingList) => ({
          ...priceData,
          // id: priceData?._id,
          date: ConvertUTCtoDate(priceData?.createdAt),
          totalRevenue: Number(priceData?.totalRevenue).toFixed(2),
          // time: `${FormatUtcString(ConvertUTCtoHHMM(priceData?.startDateUtc))} - ${FormatUtcString(ConvertUTCtoHHMM(priceData.endDateUtc))}`,
        }));
      })
      .addCase(fetchAllDynamicPrice.rejected, (state) => {
        state.loading = false;
      });
  },
});

// Export the actions
export const {
  setOffPeakDynamicFilterData,
  resetOffPeakToInitialState,
  removeDateFilters,
  setSortData,
} = dynamicOffPeakPriceSlice.actions;

// Export the reducer
export default dynamicOffPeakPriceSlice.reducer;
